import { useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { AcceptRequest, TenantAddonView } from '@/api/generated';
import { backendCode } from '@/api/errors';
import { CardCapture } from '@/features/billing/CardCapture';
import { isCardDeclinedCode } from '@/features/billing/cardDeclined';
import { useLocale } from '@/i18n/useLocale';
import { useTenantCurrency } from '@/tenant/useTenant';
import { allowedBillingModes, defaultBillingMode, priceLabel } from './addonLabels';
import { SubmitButton } from '@/components/SubmitButton';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { useActionError, type ActionError } from '@/components/useActionError';

export interface AddonAcceptDialogProps {
  addon: TenantAddonView | null;
  lastBilling: AcceptRequest['billingMode'] | null;
  onClose: () => void;
  onAccept: (id: number, body: AcceptRequest) => Promise<void>;
  onRememberBilling: (mode: AcceptRequest['billingMode']) => void;
}

export function AddonAcceptDialog({
  addon,
  lastBilling,
  onClose,
  onAccept,
  onRememberBilling,
}: AddonAcceptDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const currency = useTenantCurrency();
  const noCurrency = currency === '';
  const [mode, setMode] = useState<AcceptRequest['billingMode']>('CARD');
  const [submitting, setSubmitting] = useState(false);
  const describe = useActionError((err, translate) => {
    const code = backendCode(err);
    if (code === 'ADDON_STATE') return translate('addons.error.state');
    if (code === 'ADDON_NETTING_DISABLED') return translate('addons.error.nettingDisabled');
    return null;
  });
  const [error, setError] = useState<ActionError | null>(null);
  // Reset only when a different add-on (or remembered choice) opens. A reload of the same
  // offer must keep the error and a switch to Card.
  const openKey = addon ? `${addon.id}:${lastBilling ?? ''}` : '';
  const [openedKey, setOpenedKey] = useState<string | null>(null);
  if (openKey !== (openedKey ?? '')) {
    setOpenedKey(openKey);
    if (addon) {
      setMode(defaultBillingMode(addon.allowedBilling, lastBilling));
      setError(null);
    }
  }

  const open = addon !== null;
  const modes = addon ? allowedBillingModes(addon.allowedBilling) : [];
  const price = addon ? priceLabel(addon, locale, currency) : '';

  function handleClose() {
    if (submitting) return;
    onClose();
  }

  async function handleToken(token: string): Promise<void | 'retry'> {
    if (!addon) return;
    setSubmitting(true);
    setError(null);
    try {
      await onAccept(addon.id, { billingMode: 'CARD', cardToken: token });
      onRememberBilling('CARD');
      onClose();
    } catch (err) {
      setError(describe(err));
      if (isCardDeclinedCode(err)) return 'retry';
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!addon || mode !== 'NET_FROM_PAYOUTS' || noCurrency) return;
    setSubmitting(true);
    setError(null);
    try {
      await onAccept(addon.id, { billingMode: 'NET_FROM_PAYOUTS' });
      onRememberBilling('NET_FROM_PAYOUTS');
      onClose();
    } catch (err) {
      setError(describe(err));
      if (
        backendCode(err) === 'ADDON_NETTING_DISABLED' &&
        allowedBillingModes(addon.allowedBilling).includes('CARD')
      ) {
        setMode('CARD');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <Box component="form" noValidate onSubmit={(event) => void handleSubmit(event)}>
        <DialogTitle>{t('addons.acceptTitle', { name: addon ? addon.name : '' })}</DialogTitle>
        <DialogContent>
          {noCurrency ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('common.noCurrency')}
            </Alert>
          ) : null}
          {addon?.description ? <Typography sx={{ mb: 1 }}>{addon.description}</Typography> : null}
          <Typography sx={{ mb: 2 }}>{price}</Typography>
          <FormControl disabled={noCurrency}>
            <FormLabel id="addon-pay-by">{t('addons.billing.payBy')}</FormLabel>
            <RadioGroup
              aria-labelledby="addon-pay-by"
              value={mode}
              onChange={(event) => setMode(event.target.value as AcceptRequest['billingMode'])}
            >
              {modes.includes('CARD') ? (
                <FormControlLabel
                  value="CARD"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography>{t('addons.billing.card')}</Typography>
                      <Typography variant="caption">{t('addons.billing.cardNote')}</Typography>
                    </Box>
                  }
                />
              ) : null}
              {modes.includes('NET_FROM_PAYOUTS') ? (
                <FormControlLabel
                  value="NET_FROM_PAYOUTS"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography>{t('addons.billing.netted')}</Typography>
                      <Typography variant="caption">{t('addons.billing.nettedNote')}</Typography>
                    </Box>
                  }
                />
              ) : null}
            </RadioGroup>
          </FormControl>
          {mode === 'CARD' && !noCurrency ? (
            <CardCapture
              scope="platform"
              submitLabel={t('addons.acceptAndPay', { price })}
              busy={submitting}
              onToken={handleToken}
            />
          ) : null}
          <ActionErrorAlert error={error} sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          {mode === 'NET_FROM_PAYOUTS' ? (
            // Disabled only with no currency, and the dialog says so above (TMM-B5).
            <SubmitButton type="submit" loading={submitting} disabled={noCurrency}>
              {t('addons.acceptAndPay', { price })}
            </SubmitButton>
          ) : null}
        </DialogActions>
      </Box>
    </Dialog>
  );
}
